import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Menu, X, ShoppingCart } from "lucide-react";

const navItems = [
    { label: "Courses", to: "/courses" },
    { label: "Workshops", to: "/workshops" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
];

export default function Header() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10"
        >
            <div className="container-edge flex items-center justify-between h-16">
                <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                    <span className="h-7 w-7 bg-klein flex items-center justify-center text-white font-heading font-black text-sm">
                        ai4
                    </span>
                    <span className="font-heading font-black tracking-tight text-lg">
                        faculty<span className="text-klein">.info</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-medium tracking-tight transition-colors ${
                                    isActive ? "text-klein" : "text-foreground hover:text-klein"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <Link
                        to="/cart"
                        data-testid="nav-cart"
                        aria-label="Cart"
                        className="relative inline-flex items-center justify-center h-9 w-9 border border-black/20 hover:bg-black hover:text-white transition-colors"
                    >
                        <ShoppingCart size={16} />
                        {count > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-klein text-white text-[10px] font-bold flex items-center justify-center rounded-full"
                            >
                                {count}
                            </span>
                        )}
                    </Link>
                    {user ? (
                        <>
                            <Link
                                to="/dashboard"
                                data-testid="nav-dashboard"
                                className="text-sm font-medium hover:text-klein"
                            >
                                Dashboard
                            </Link>
                            <button
                                data-testid="logout-btn"
                                onClick={() => {
                                    logout();
                                    navigate("/");
                                }}
                                className="text-sm font-medium px-4 py-2 border border-black/20 hover:bg-black hover:text-white transition-colors"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-login"
                                className="text-sm font-medium hover:text-klein"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                data-testid="nav-register"
                                className="text-sm font-semibold px-4 py-2 bg-klein text-white hover:bg-black transition-colors"
                            >
                                Get started
                            </Link>
                        </>
                    )}
                </div>

                <button
                    className="md:hidden relative"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                    {!open && count > 0 && (
                        <span data-testid="mobile-cart-count-badge" className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 bg-klein text-white text-[9px] font-bold flex items-center justify-center rounded-full">
                            {count}
                        </span>
                    )}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-black/10 bg-white">
                    <div className="container-edge py-4 flex flex-col gap-4">
                        {navItems.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                className="text-sm font-medium"
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                        <Link
                            to="/cart"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-nav-cart"
                            className="text-sm font-medium inline-flex items-center gap-2"
                        >
                            <ShoppingCart size={14} /> Cart{count > 0 && ` (${count})`}
                        </Link>
                        {user ? (
                            <>
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium">
                                    Dashboard
                                </Link>
                                <button
                                    onClick={() => {
                                        logout();
                                        setOpen(false);
                                        navigate("/");
                                    }}
                                    className="text-left text-sm font-medium"
                                >
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium">
                                    Sign in
                                </Link>
                                <Link
                                    to="/register"
                                    onClick={() => setOpen(false)}
                                    className="text-sm font-semibold bg-klein text-white px-4 py-2 inline-block w-fit"
                                >
                                    Get started
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
